import { host, applyRefreshedToken } from '../context/Utils';

/**
 * HTTP error thrown by apiFetch. Includes the response status and the
 * parsed body (when JSON-parseable) so consumers can show meaningful
 * error messages without re-parsing.
 */
export class HttpError extends Error {
  constructor(status, body, statusText) {
    // express-validator returns { errors: [{ msg, path, ... }] } on
    // 400; surface the first message so consumers don't have to know
    // about the array shape. Validator messages are MORE specific
    // than a generic top-level error string, so they take priority.
    const validatorMsg = Array.isArray(body?.errors) ? body.errors[0]?.msg : null;
    super(
      validatorMsg
      || body?.error
      || body?.message
      || statusText
      || `HTTP ${status}`
    );
    this.status = status;
    this.body = body;
  }
}

/**
 * Single fetch wrapper used by every React Query hook.
 *
 * - Adds the `authtoken` header from localStorage if present.
 * - Auto-stringifies non-string bodies and sets Content-Type.
 * - Reads `x-refreshed-token` from the response (sliding expiry).
 * - On 401: clears the local token and dispatches an `auth:expired`
 *   window event so AuthState (or any subscriber) can react.
 * - On non-2xx: throws HttpError with the parsed body.
 * - On 204: returns null instead of trying to parse JSON.
 */
export const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers.authtoken = token;
  if (options.body !== undefined && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const url = path.startsWith('http') ? path : `${host}${path}`;
  const init = { ...options, headers };
  if (init.body !== undefined && typeof init.body !== 'string') {
    init.body = JSON.stringify(init.body);
  }

  const res = await fetch(url, init);
  applyRefreshedToken(res);

  if (res.status === 401) {
    localStorage.removeItem('token');
    window.dispatchEvent(new CustomEvent('auth:expired'));
  }

  if (!res.ok) {
    let body = null;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new HttpError(res.status, body, res.statusText);
  }

  if (res.status === 204) return null;
  // Some endpoints (root /, simple HTML) won't be JSON. Be defensive.
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  const json = await res.json();
  // Server convention: 2xx bodies are { success: true, data: <thing> }.
  // Auto-unwrap so consumers read the unwrapped shape directly. Any
  // response that doesn't follow the convention falls through unchanged.
  if (json && typeof json === 'object' && json.success === true && 'data' in json) {
    return json.data;
  }
  return json;
};
